import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { Box, Button, ButtonGroup, Divider, IconButton, InputAdornment, Paper, SxProps, Theme, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import moment from 'moment';
import { useState } from "react";
import { RequestStatusChip } from '.';
import route from '../../Router';
import TabPanel from '../../components/TabPanel';
import { AbstractTypedDataForm } from "../../components/data/DataForm";
import { FormTextInput, TextInput } from "../../components/form/TextInput";
import { ActivityType, RequestStatus, requestStatuseMap } from "../../models/Enums";
import { hasKey } from '../../models/Identity';
import Proxy from '../../models/Proxy';
import { getTenant } from '../../models/Tenant';
import { Request, RequestActivity } from "../../models/Types";
import { isMobile } from "../../utils";
import HouseCombo from "../houses/HouseCombo";
import InhabitantCombo from "../inhabitants/InhabitantCombo";
import ServiceCombo, { serviceStore } from "../project/service/ServiceCombo";
import { SendRounded } from '@mui/icons-material';

export function requestParse(d?: any) {
    const dto = (d || {}) as Request;
    d?.CreateDate && (dto.CreateDate = new Date(d.CreateDate));
    return dto;
}

function ActivityTimeline({ entity }: { entity: Request }) {
    const [com, scom] = useState('');
    function addComment() {
        if (com) {
            Proxy.post('RequestAddComment', {
                RequestId: entity.Id,
                Description: com
            } as RequestActivity).then(x => {
                if (x.success) {
                    (entity.Activities || (entity.Activities = [])).push(x.result);

                    scom('');
                }
            });
        }
    }

    return <>
        <Timeline
            sx={{
                p: 1,
                [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                },
            }}>
            {entity.Activities?.sort((a, b) => a.CreateDate > b.CreateDate ? 1 : -1)
                .reduce((r, x) => {
                    if (x.Type === ActivityType.Create || x.Type === ActivityType.StatusChange) {
                        if (x.Description && x.Description[x.Description.length - 1] === '"') {
                            x.Description = x.Description.substring(0, x.Description.length - 1);
                            x.Description = x.Description.substring(x.Description.lastIndexOf('"') + 1);
                        }

                        r.splice(0, 0, [x]);
                    } else {
                        r[0].push(x);
                    }

                    return r;
                }, [] as RequestActivity[][])
                .map((x, i, arr) => <TimelineItem>
                    <TimelineSeparator>
                        <TimelineDot color="primary" sx={{ m: 0 }} />
                        {i === arr.length - 1 ? null : <TimelineConnector sx={{ bgcolor: 'primary' }} />}
                    </TimelineSeparator>

                    <TimelineContent sx={{ pt: 0, pb: 2, px: 2 }}>
                        {x.map((x, i) => <>
                            <Typography variant={i ? 'menu' : 'h3'}>{x.Description}</Typography>
                            <Typography variant='body2' component="p" mt={.5} mb={2}>{moment(new Date(x.CreateDate)).calendar()} • {x.Person}</Typography>
                        </>)}
                    </TimelineContent>
                </TimelineItem>)}
        </Timeline>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Box>
            <TextInput label="Добавить комментарий" name="Comment" onChange={e => scom(e.target.value)} inputProps={{
                multiline: true,
                value:com,
                endAdornment: isMobile() ? <Button sx={{ minWidth: 0 }} onClick={addComment} disabled={!com?.trim()}><SendRounded /></Button>:undefined
            }} />

            {isMobile() ? undefined : <Box sx={{ display: 'flex', my: 2 }}>
                <div style={{ flex: 1 }} />
                {<Button variant="contained" onClick={addComment}>Добавить комментарий</Button>}
            </Box>}
        </Box>
    </>;
}

function changeRequestStatus(entity: Request, current: RequestStatus, status: RequestStatus, callback: (status: RequestStatus) => any) {
    const nst = (entity.Status = parseInt(status) as RequestStatus || undefined);
    if (current != nst) {
        Proxy.post('RequestChangeStatus?id=' + entity.Id, nst).then(x => {
            if (x.success) {
                callback(nst || 0);

                (entity.Activities || (entity.Activities = [])).push(x.result);
            }
        });
    }
}

function ActivityForm({ entity }: { entity: Request }) {
    const [st, sst] = useState(entity.Status || 0);

    function changeStatus(e: any, value: string) {
        changeRequestStatus(entity, st, parseInt(value) as RequestStatus || undefined, x => sst(x));
    }

    const isEmp = hasKey('Request');
    return <Paper style={isMobile() ? { flex: 'none' } : { flex: 1 }}>
        <Box>
            <Typography variant="h6">Активность</Typography>

            <ToggleButtonGroup color="primary"
                value={st.toString()}
                size="small"
                exclusive
                onChange={changeStatus}>
                {requestStatuseMap.map((x, i) => <ToggleButton value={x[0].toString()} disabled={!i || (!!isEmp === !!x[2])}>{x[1]}</ToggleButton>)}
            </ToggleButtonGroup>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        <ActivityTimeline entity={entity} />
    </Paper>;
}

export class RequestForm extends AbstractTypedDataForm<Request, { service?: number }> {
    protected getTypeId(): string {
        return 'Request';
    }
    protected getTypeName(): React.ReactNode {
        return 'Заявка';
    }

    buildButtons() {
        return [];
    }

    protected createEntity() {
        const result = requestParse();
        if (this.props.service) {
            result.ServiceIds = [this.props.service];
            
            result.Name = serviceStore.data?.find(x => x.Id == this.props.service)?.Obj?.Name;
            if (!result.Name) {
                serviceStore.onLoad(d => {
                    this.onFieldChange('Name', d?.find(x => x.Id == this.props.service)?.Obj?.Name);
                    this.forceUpdate();
                });
                serviceStore.load();
            }
        }

        const houses = getTenant()?.Houses;
        if (houses?.length) {
            result.HouseIds = houses.map(x => x.Id);
        }

        result.ApplicantId = getTenant()?.Person?.Id;

        return result;
    }

    getDeleteProps() {
        return {
            ...super.getDeleteProps(),
            title: `Удалить заявку №${this.state.entity.Id}?`,
            text: 'После удаления заявку нельзя будет восстановить'
        };
    }

    protected getFormStyle(): SxProps<Theme> | undefined {
        if (isMobile()) {
            return {
                ...super.getFormStyle(),
                flexDirection: 'column',
                alignItems: 'stretch',
                overflow: 'hidden'
            };
        } else {
            return super.getFormStyle();
        }
    }

    protected buildItems(entity?: Request | undefined): React.ReactNode {
        const ism = isMobile();
        const disabled = this.isReadOnly();

        const fields = <>
            <FormTextInput key={`Name-${entity?.Name}`} label="Наименование" name="Name" form={this} entity={entity} required={true} inputProps={{ disabled }} />

            <TextInput key={`ApplicantId-${entity?.Id}`}
                value={entity?.ApplicantId}
                label="Заявитель"
                name="ApplicantId"
                onChange={this.onChange}
                component={(onChange, error) => <InhabitantCombo
                    name="ApplicantId"
                    error={!!error}
                    value={entity?.ApplicantId}
                    disabled={disabled || !hasKey('Request')}
                    onChange={onChange} />} />

            <TextInput key={`ServiceIds-${entity?.Id}`}
                value={entity?.ServiceIds}
                label="Услуги"
                name="ServiceIds"
                onChange={this.onChange}
                component={(onChange, error) => <ServiceCombo
                    name="ServiceIds"
                    multiple={true}
                    error={!!error}
                    value={entity?.ServiceIds}
                    disabled={disabled}
                    onChange={onChange} />} />

            <TextInput key={`HouseIds-${entity?.Id}`}
                value={entity?.HouseIds}
                label="Домовладение(я)"
                name="HouseIds"
                onChange={this.onChange}
                required={true}
                component={(onChange, error) => <HouseCombo
                    name="HouseIds"
                    multiple={true}
                    required={true}
                    error={!!error}
                    value={entity?.HouseIds}
                    disabled={disabled}
                    onChange={onChange} />} />

            <FormTextInput label="Описание" name="Description" form={this} entity={entity} inputProps={{ multiline: true, disabled }} />
            <Typography variant="caption" color="textSecondary">Опишите в свободной форме, объем услуг и другие примечания</Typography>
        </>;

        if (this.isReadOnly()) {
            return <Box mt={2} display="flex" flexDirection="column" gap={2}>{fields}</Box>;
        }

        return <>
            <Paper
                elevation={ism ? 0 : undefined}
                style={ism ? { height: '100%', display: 'flex', flex: 1, flexDirection: 'column', overflow: 'hidden' } : { minWidth: 500, flex: 'none' }}>
                <Box overflow="auto" maxWidth={500} display="flex" flex={1} flexDirection="column" gap={2}>
                    {fields}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    {ism ? <Button variant="outlined" onClick={() => route.setState(entity?.Id ? [['edit']] : [['edit'], ['id']])}>Отменить</Button> : <div style={{ flex: 1 }} />}
                    {(entity && (!entity.Id || hasKey('Request'))) ? <Button variant="contained" type="submit">Сохранить</Button> : null}
                </Box>
            </Paper>

            {entity?.Id && !ism ? <ActivityForm entity={entity} /> : null}
        </>;
    }
}

export class MobileRequestForm extends RequestForm {

    protected getFormStyle(): SxProps<Theme> | undefined {
        if (isMobile()) {
            return {
                ...super.getFormStyle(),
                flexDirection: 'column',
                alignItems: 'stretch',
                overflow: 'auto'
            };
        } else {
            return super.getFormStyle();
        }
    }

    protected buildItems(entity?: Request): React.ReactNode {
        return entity?.Id ? <Box p={2}>
            <Box mb={1}>
                <Typography variant="h2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    {entity?.Name}
                    {entity && <RequestStatusChip status={entity.Status} variant="body2" />}
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, alignItems: 'center' }}>
                    Заявка № {entity?.Id}
                    {entity?.Status === RequestStatus.Done ? <>
                        <ButtonGroup size="small">
                            <Button color="success" onClick={() => changeRequestStatus(entity, entity.Status, RequestStatus.Completed, () => this.forceUpdate())}>Принять работу</Button>
                            <Button color="error" onClick={() => changeRequestStatus(entity, entity.Status, RequestStatus.Rejected, () => this.forceUpdate())}>Заявка не выполнена</Button>
                        </ButtonGroup>
                    </>:null}
                </Typography>
            </Box>
            {entity &&
                <TabPanel sx={{
                    height: 'auto',
                    flex: 1
                }} tabs={[{
                    title: 'Статус заявки',
                    element: () => <ActivityTimeline entity={entity!} />
                }, {
                    title: 'Данные заявки',
                    element: () => <RequestForm id={entity.Id} entity={entity!} readOnly={true} />
                }]} />}
        </Box> : entity ? super.buildItems(entity) : null;
    }
}